import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@sharedcommon/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { HeaderService } from '@sharedcommon/services/header.service';
import { ValidationService } from '@sharedcommon/services/validation.service';
import { Subscription } from 'rxjs';
import { NeqAbstractFormViewComponent } from '@sharedcommon/components/validation/abstract-form-view.component';
import { SampleRestService } from '@sharedcommon/rest/sample/sample.restservice';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { NeqSampleMandatoryProperties } from '@usercommon/components/samples/samples-mandatory-properties/samples-mandatory-properties.component';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { NeqDialogService } from '@sharedcommon/services/dialog.service';
import { ReferenceDto } from '@sharedcommon/rest/data.restservice';
import { EntityDto } from '@sharedcommon/rest/identity.restservice';
import { SupplierDto } from '@sharedcommon/model/organization/supplier.model';
import { NeqSampleOptionalProperties } from '@usercommon/components/samples/samples-optional-properties/samples-optional-properties.component';
import { SampleFarmDto } from '@sharedcommon/model/sample/sample-farm.model';
import { SampleCustomerDto } from '@sharedcommon/model/sample/sample-customer.model';
import { SampleSupplierDto } from '@sharedcommon/model/sample/sample-supplier.model';
import { CustomerDto } from '@sharedcommon/model/organization/customer.model';
import { BusinessUnitDto } from '@sharedcommon/model/organization/business-unit.model';
import { ImportType } from '@sharedcommon/model/import-type.model';
import { FunctionUtils } from '@sharedcommon/util/functions.util';


@Component({
  selector: 'neq-samples-properties',
  templateUrl: './samples-properties.page.html'
})
export class NeqSamplesPropertiesPage extends NeqAbstractFormViewComponent implements OnInit, OnDestroy, AfterContentChecked {

  readonly ERROR_READ_ONLY = 'readonly';

  /** the form for mandatory properties */
  mandatoryForm: FormGroup;
  /** the form for mandatory properties */
  optionalForm: FormGroup;
  /** the form for mandatory AND optional properties */
  globalForm: FormGroup;
  notesForm: FormControl;

  canUpdate: boolean;
  /**
   * Indicate if there is a linked business unit to the entity
   */
  isThereLinkedBu: boolean = false;
  selectedEntity: EntityDto = null;
  selectedReference: ReferenceDto = null;
  selectedSupplier: SupplierDto = null;
  selectedBusinessUnit: BusinessUnitDto = null;
  businessUnitFromMainEntity: BusinessUnitDto = null;
  selectedCustomer: CustomerDto = null;
  mandatoryFormAlreadyFilled: boolean = false;

  sample: SampleDto;

  private subscriptionForUrlParams: Subscription;

  constructor(
    private readonly sampleService: SampleRestService,
    public toastService: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router:Router,
    private readonly translate: TranslateService,
    private readonly headerService: HeaderService,
    private readonly dialogService: NeqDialogService,
    protected validation: ValidationService,
    private readonly cd: ChangeDetectorRef
  ) {
    super(validation);
    this.validationService.init();
  }

  ngOnInit() {
    this.mandatoryForm = NeqSampleMandatoryProperties.createEmptyMandatoryForm();
    this.optionalForm = NeqSampleOptionalProperties.createEmptyOptionalForm();
    this.notesForm = new FormControl();
    this.globalForm = new FormGroup({
      // Add the different forms to be able to see if the complete form is tocuhed, valid, etc...
      "mandatoryForm": this.mandatoryForm,
      "optionalForm": this.optionalForm,
      "notes": this.notesForm
    });
    this.checkInForm(this.globalForm);
    this.subscriptionForUrlParams = this.route.params
      .subscribe(params => {
        const idToEdit = params['id'];
        if (!idToEdit) {
          this.updateHeaderOfPage(null);
        } else {
          this.getSampleByIdAndUpdateHeader(idToEdit);
        }
      });
  }



  ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  /**
   * Overerided method from ${@link NeqAbstractFormViewComponent}
   */
  getFormGroup(): FormGroup {
    return this.globalForm;
  }

  /**
   * Update the meta on init
   *
   * @param sampleId the sample ID to retrieve
   */
  getSampleByIdAndUpdateHeader(sampleId: string): void {
    this.sampleService.getSampleById(sampleId)
      .subscribe(sample => {
        // Mark it as pristine in case the user has already enter some values
        // and to indirectly indicate to the sub component to initialize the form
        // with the sample attribute
        this.globalForm.markAsPristine();
        // Prefill the form here in case the user is not in the corresponding tab form
        // It will allow us to keep the data of the retrieved sample even
        // if the user do not visit the tab
        NeqSampleMandatoryProperties.fillMandatoryForm(sample, this.mandatoryForm);
        NeqSampleOptionalProperties.fillOptionalForm(sample, this.optionalForm);
        this.sample = sample;
        this.notesForm.setValue(sample.notes);
        this.updateHeaderOfPage(sample);
      });
  }

  save(): void {
    if (this.globalForm.invalid) {
      // Mark all as touched to visually see in the screen
      this.globalForm.markAllAsTouched();
      this.showFormPopinError();
      return;
    }
    const mandatSample = NeqSampleMandatoryProperties.returnSampleFromForm(this.mandatoryForm);
    const optionalSample = NeqSampleOptionalProperties.returnSampleFromForm(this.optionalForm);
    // Merge to keep former value if not setted by the forms and also, in some cases,
    // merge values from mandatory form AND optional form
    const farm: SampleFarmDto = { ...(this.sample?.farm ?? new SampleFarmDto()), ...mandatSample.farm, ...optionalSample.farm };
    const customer: SampleCustomerDto = {
      ...(this.sample?.customer ?? new SampleCustomerDto()),
      ...optionalSample.customer,
      urn: mandatSample.customer.urn
    };
    const supplier: SampleSupplierDto = { ...(this.sample?.supplier ?? new SampleSupplierDto()), ...optionalSample.supplier };
    const sample: SampleDto = {
      ...(this.sample ?? new SampleDto()),
      ...mandatSample,
      ...optionalSample,
      notes: this.notesForm.value,
      farm: farm,
      customer: customer,
      supplier: supplier,
      // Keep the former value or set it as MANUAL
      originalImportType: this.sample?.originalImportType ?? ImportType.MANUAL
    };
    if (sample.id) {
      this.sampleService.update(sample.id, sample, true)
        .subscribe({
            next: updatedSample =>  {
              this.toastService.toast(this.translate.instant('msg.user.sample.updated'));
              this.navigateToInputPage(updatedSample.id);
            },
            error: (error: HttpErrorResponse) => {
              this.handleCreateOrUpdateHttpError(error);
            }
        });
    } else {
      this.sampleService.create(sample, true)
        .subscribe({
          next : newSample => {
            this.toastService.toast(this.translate.instant('msg.user.sample.created'));
            this.navigateToInputPage(newSample.id);
          },
          error: (error: HttpErrorResponse) => {
            this.handleCreateOrUpdateHttpError(error);
          }
        });
    }
  }

  /**
   * Handle http error in case of creation and update http error
   *
   * @param error the error returned by the API
   */
  handleCreateOrUpdateHttpError(error: HttpErrorResponse): void {
    if (error.status === HttpStatusCode.Conflict) {
      this.dialogService.showErrorSimpleOneMessage(this.translate.instant("error.user.sample.duplicated-sample"));
      return;
    }
    // Retrow the error to get the built-in popin error msg
    throw error;
  }

  private navigateToInputPage(sampleId: string): void {
    this.router.navigateByUrl(`/app/samples/input/${sampleId}`);
  }

  /**
   * Show pop in error for form
   */
  private showFormPopinError(): void {
    const tabWhereError: string[] = this.mandatoryForm.invalid ? [this.translate.instant('msg.common.mandatory-informations-tab')] : [];
    if (this.optionalForm.invalid) {
      tabWhereError.push(this.translate.instant('msg.common.optional-informations-tab'));
    }
    const tabErrorMsg = tabWhereError.length > 0 ?
                          ` ${this.translate.instant('msg.common.in')} ${tabWhereError.join(` ${this.translate.instant('msg.common.and')} `)}`
                          : '';
    this.dialogService.showInformation(` ${this.translate.instant('error.user.sample.form-error-intro')} ${tabErrorMsg}`)
  }

  /**
   * Update the header of the web page according internal data
   *
   * @param sample  the raw material from what the header can be filled
   */
  private updateHeaderOfPage(sample: SampleDto | null): void {
    if (sample) {
      this.headerService.setHeaderValues({
        strongLabel: sample.code,
        basicLabel: ' - ' + sample.label,
        showGoBack: true,
        redirectionRoute:  `/app/samples/input/${sample.id}`
      });
    } else {
      this.headerService.setHeaderValues({
        basicLabel: this.translate.instant('label.user.sample.create.title'),
        showGoBack: true,
        redirectionRoute: this.route.parent.snapshot
      });
    }
  }

  isGlobalFormNotPristineAndValid(): boolean {
    const pristineForm = this.globalForm?.pristine;
    return FunctionUtils.isDefined(pristineForm) && !pristineForm && !!this.globalForm?.valid;
  }

  ngOnDestroy(): void {
    this.subscriptionForUrlParams.unsubscribe();
  }
}
